import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import createSafeContext from '@paradigm/utils/createSafeContext';
import {
  Volume,
  RawVolume,
  VolumeListReq,
} from '#/leaderboard/entities/volume';
import {
  MOBILE_VIEW_VOLUME_OPTIONS,
  UserFirm,
} from '#/leaderboard/entities/shared';
import {
  useVolumesFilterActions,
  useVolumesFilterState,
} from '#/leaderboard/contexts/volumes-filter';
import { processVolume } from '#/leaderboard/repositories/adapters';
import { listLeaderboardVolume } from '#/leaderboard/repositories/volume';
import { useStoredLeaderboardFetcher } from '#/leaderboard/contexts/fetcher';

type VolumeMobileView = typeof MOBILE_VIEW_VOLUME_OPTIONS[number];
interface VolumesListState {
  readonly volumes: Volume[];
  readonly userFirm: UserFirm | null;
  readonly status: 'loading' | 'idle' | 'error';
  readonly mobileView: VolumeMobileView;
  readonly lastUpdatedAt: number | null;
  readonly error: string | null;
}

export const [useVolumesListState, VolumesListStateProvider] =
  createSafeContext<VolumesListState>('VolumesListState');

interface VolumesListActions {
  readonly fetchVolumesList: () => void;
  readonly changeMobileView: (view: VolumeMobileView) => void;
}

export const [useVolumesListActions, VolumesListActionsProvider] =
  createSafeContext<VolumesListActions>('VolumesListActions');

export default function VolumesListProvider({ children }: PropsWithChildren) {
  const filterActions = useVolumesFilterActions();
  const [mobileView, setMobileView] = useState<VolumeMobileView>('Overall');

  const {
    status,
    error,
    list,
    userFirm,
    lastUpdatedAt,
    fetchLeaderboard: fetchVolumesList,
  } = useStoredLeaderboardFetcher<RawVolume, VolumeListReq>({
    filterActions,
    fetchFn: listLeaderboardVolume,
    key: 'volumes',
  });

  const filters = useVolumesFilterState();

  const state = useMemo(
    () => ({
      volumes: list.map((v) => processVolume(v, userFirm)),
      status,
      lastUpdatedAt,
      error,
      userFirm,
      mobileView,
    }),
    [list, status, lastUpdatedAt, error, userFirm, mobileView],
  );

  const actions: VolumesListActions = useMemo(
    () => ({
      fetchVolumesList: () => fetchVolumesList(filters),
      changeMobileView: setMobileView,
    }),
    [fetchVolumesList, filters],
  );

  useEffect(() => {
    actions.fetchVolumesList();
  }, [actions]);

  return (
    <VolumesListStateProvider value={state}>
      <VolumesListActionsProvider value={actions}>
        {children}
      </VolumesListActionsProvider>
    </VolumesListStateProvider>
  );
}
